import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { HostingSitesV2200ItemsItem } from '@newfold/huapi-js/src/index.schemas';

import { useAlert } from '~/components/Alerts/alertsStore';
import HostingSSO from '~/components/SSO/components/HostingSSO';
import SiteSSO from '~/components/SSO/components/SiteSSO';
import SSOLink from '~/components/SSOLink';
import { generateVPSAppButtonTestId } from '~/Nova/scenes/HostingList/utils/helpers';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';
import { setAlertMessage } from '~/utils/common/helpers';
import { STATUSES } from '~/utils/site/constants';

import AppLogo from '../../../components/AppLogo';
import InfoCard from '../../../components/InfoCard';
import {
  CPANEL,
  DISABLED_REIMAGE_STATUSES,
  REIMAGE_IN_PROGRESS_STATUSES,
} from '../../../utils/constants';
import { getLogoConfig } from '../../../utils/imageLogos';

interface OSManageCardProps {
  installedAppData: HostingSitesV2200ItemsItem | undefined;
  installedAppDataLoading: boolean;
  isServerOnline: boolean;
  domainName: string;
  osName?: string | null;
  osVersion?: string | null;
  isReimaging?: boolean;
  isOsImagesAvailable: boolean;
  isErrorOSImages: boolean;
  serverStatus: string;
  isServerDataPending: boolean;
  appActionInProgress: boolean;
  installedSoftware?: string;
  hostingId: string;
  installedApp?: string | null;
  ip?: string;
  osAdvancedImages?: PublicImageItem[];
}

const OSManageCard = ({
  installedAppData,
  installedAppDataLoading,
  isServerOnline,
  osName = '',
  osVersion = '',
  isReimaging = false,
  isOsImagesAvailable,
  isErrorOSImages,
  serverStatus,
  isServerDataPending,
  appActionInProgress,
  installedSoftware = '',
  installedApp = '',
  hostingId,
  osAdvancedImages = [],
}: OSManageCardProps) => {
  const { t } = useTranslation('selfManagedHosting', { keyPrefix: 'overview' });
  const generateAlert = useAlert();
  const navigate = useNavigate();

  const {
    subtype,
    status: appStatus = '',
    id: siteId,
  } = installedAppData || {};
  const isAppInstallFailed = STATUSES.FAILED.includes(appStatus);
  const installedCurrentApp = osAdvancedImages.find(
    (img) =>
      img.app?.toLowerCase() === installedApp?.toLowerCase() ||
      img.software?.toLowerCase() === installedApp?.toLowerCase(),
  );
  const displayAppName =
    installedSoftware.toLowerCase() === 'cpanel'
      ? installedSoftware
      : subtype ?? installedApp ?? '';
  const {
    manage_link: manageLink,
    software,
    app: appName,
  } = installedCurrentApp || {};

  const handleReImage = () => {
    if (isOsImagesAvailable || isErrorOSImages) {
      setAlertMessage(t('card.osImageErrorMsg'), generateAlert, 'error');
    } else {
      navigate('../reimage-server/plain-os');
    }
  };

  const handleApplicationManage = (manageLink?: string) => {
    if (manageLink) {
      window.open(manageLink, '_blank', 'noopener,noreferrer');
    }
  };

  const appCardPrimaryItem = (() => {
    const isServerReimaging =
      REIMAGE_IN_PROGRESS_STATUSES.includes(serverStatus);
    const isAppActive = appStatus === 'active';
    const hasApp = Boolean(installedAppData && subtype && !isAppInstallFailed);

    if (
      installedAppDataLoading ||
      isServerDataPending ||
      !osName ||
      isServerReimaging
    ) {
      return <Skeleton variant="text" width={100} />;
    }

    const osLogoResult = getLogoConfig(osName);
    const { logo: osLogo = '', logoAlt: osLogoAlt = '' } = osLogoResult || {};

    const osImageMeta = osAdvancedImages.find(
      (img) => img.os_name === osName && img.type === 'base',
    );
    const osDisplayName = osImageMeta?.os_name ?? osName;
    const osDisplayText = osVersion
      ? `${osDisplayName} ${osVersion}`
      : osDisplayName;

    const showAppName = hasApp && isAppActive;
    const isCpanelInstalled = installedSoftware.toLowerCase() === 'cpanel';
    const showSoftwareInfo = isCpanelInstalled && !showAppName;

    const appImageMeta = osAdvancedImages.find(
      (img) =>
        img.app === displayAppName ||
        img.software?.toLowerCase() === displayAppName.toLowerCase(),
    );
    const hasAppMeta = Boolean(appImageMeta || displayAppName);
    const showApplicationInfo = installedApp && hasAppMeta && !showAppName;

    const appLogoConfig = (() => {
      const appLogoResult = getLogoConfig(displayAppName);
      const { logo: appLogo = '', logoAlt: appLogoAlt = '' } =
        appLogoResult || {};
      return {
        key: displayAppName,
        name: appImageMeta?.title ?? displayAppName,
        logo: appLogo,
        logoAlt: appLogoAlt,
        size: {
          small: { width: 25, height: 25 },
          large: { width: 40, height: 32 },
        },
      };
    })();

    const logoConfig =
      showAppName || showSoftwareInfo || showApplicationInfo
        ? appLogoConfig
        : {
            key: osName,
            name: osDisplayText,
            logo: osLogo,
            logoAlt: osLogoAlt,
            size: {
              small: { width: 25, height: 25 },
              large: { width: 40, height: 38 },
            },
          };

    if (!logoConfig?.name) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="center">
        <AppLogo logoConfig={logoConfig} variant="small" />
        {(showAppName || showSoftwareInfo || showApplicationInfo) && (
          <Typography variant="body2" sx={{ pl: 1 }}>
            {t('card.onOS', { os: osDisplayText })}
          </Typography>
        )}
      </Stack>
    );
  })();

  const appCardSecondaryItem = (() => {
    if (isReimaging || appActionInProgress) {
      return (
        <Button
          variant="outlined"
          disabled
          data-testid="reimaging-disabled-btn"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} color="inherit" />
            <Typography>{t('card.reimaging')}</Typography>
          </Stack>
        </Button>
      );
    }

    const reimageButton = (
      <Button
        variant="outlined"
        disabled={
          isServerDataPending ||
          DISABLED_REIMAGE_STATUSES.includes(serverStatus)
        }
        onClick={handleReImage}
        data-testid="reimage-btn"
      >
        {t('card.reImage')}
      </Button>
    );
    const manageButton = (() => {
      if (isAppInstallFailed) {
        return null;
      }
      if (software?.toLowerCase() === CPANEL) {
        return (
          <SSOLink
            variant="contained"
            type="vps"
            queryParams={{ app: 'whm' }}
            hostingId={hostingId}
            data-testid="sso-cpanel-manage-btn"
          >
            {t('card.manage')}
          </SSOLink>
        );
      }
      if (manageLink === 'sso') {
        return siteId != null ? (
          <SiteSSO
            variant="contained"
            siteId={siteId}
            data-testid={generateVPSAppButtonTestId(
              appName!,
              'site-sso-manage',
            )}
          >
            {t('card.manage')}
          </SiteSSO>
        ) : (
          <HostingSSO
            hostingId={hostingId}
            variant="contained"
            data-testid={generateVPSAppButtonTestId(
              appName!,
              'hosting-sso-manage',
            )}
          >
            {t('card.manage')}
          </HostingSSO>
        );
      }

      if (manageLink) {
        return (
          <Button
            variant="contained"
            onClick={() => handleApplicationManage(manageLink ?? '#')}
            disabled={!isServerOnline}
            data-testid="manage-btn"
          >
            {t('card.manage')}
          </Button>
        );
      }

      return null;
    })();

    if (manageButton) {
      return (
        <Stack direction="row" spacing={2}>
          {reimageButton}
          {manageButton}
        </Stack>
      );
    }

    return reimageButton;
  })();

  return (
    <InfoCard
      title={t('card.title.serverImage')}
      primaryItem={appCardPrimaryItem}
      secondaryItem={{
        itemNode: appCardSecondaryItem,
        type: 'button',
      }}
    />
  );
};

export default OSManageCard;
